/** Broker fee schedule + helpers for opportunity profitability. */

export type FeeSchedule = {
  scope: string;
  equityOnline: number;
  optionPerContract: number;
  optionRegPerContract: number;
  /** Underlying root (RUT, SPX, …) → $/contract proprietary index fee. */
  indexFees: Record<string, number>;
  /** Commission to close one contract. $0 at tastytrade; Schwab matches open. */
  optionClosePerContract: number;
  /** Dollar cap on opening commission per equity/ETF leg. null = no cap. */
  optionPerLegCap: number | null;
};

/** Temporary: VSE scan P/L and ROR ignore commissions. */
export const ZERO_FEE_SCHEDULE: FeeSchedule = {
  scope: "zero",
  equityOnline: 0,
  optionPerContract: 0,
  optionRegPerContract: 0,
  indexFees: {},
  optionClosePerContract: 0,
  optionPerLegCap: null,
};

export const DEFAULT_FEE_SCHEDULE: FeeSchedule = {
  scope: "default",
  equityOnline: 0,
  optionPerContract: 0.65,
  optionRegPerContract: 0.01,
  indexFees: { RUT: 0.15 },
  optionClosePerContract: 0.65,
  optionPerLegCap: null,
};

/** tastytrade published pricing (Aug 2026). See docs/tastytrade/commissions-and-fees.md. */
export const DEFAULT_TASTYTRADE_FEE_SCHEDULE: FeeSchedule = {
  scope: "tastytrade",
  equityOnline: 0,
  optionPerContract: 1.0,
  optionRegPerContract: 0.12,
  indexFees: {
    SPX: 0.6,
    CBTX: 0.5,
    OEX: 0.4,
    XEO: 0.4,
    VIX: 0.35,
    MBTX: 0.25,
    NDX: 0.25,
    DJX: 0.18,
    RUT: 0.18,
  },
  optionClosePerContract: 0,
  optionPerLegCap: 10,
};

export function isTastytradeScope(scope: string): boolean {
  return scope === "tastytrade" || scope.startsWith("tastytrade:");
}

export function defaultScheduleForScope(scope: string): FeeSchedule {
  if (isTastytradeScope(scope)) {
    return { ...DEFAULT_TASTYTRADE_FEE_SCHEDULE, scope };
  }
  return { ...DEFAULT_FEE_SCHEDULE, scope };
}

/** Fill close-commission / per-leg cap from the broker encoded in `scope`. */
export function applyBrokerRules(s: FeeSchedule): FeeSchedule {
  if (isTastytradeScope(s.scope)) {
    return { ...s, optionClosePerContract: 0, optionPerLegCap: 10 };
  }
  return { ...s, optionClosePerContract: s.optionPerContract, optionPerLegCap: null };
}

/** Normalize underlying for index-fee lookup ($RUT / RUTW → RUT). */
export function feeRoot(underlying: string): string {
  let u = underlying.trim().replace(/^\$/, "").toUpperCase();
  if (u === "NDXP") return "NDX";
  // Weekly roots often append W (RUTW, SPXW); fee is on the cash index.
  if (u.length > 1 && u.endsWith("W") && !["MSFT", "NVDA"].includes(u)) {
    // Only strip trailing W when the rest looks like a known index root (3–4 letters).
    const base = u.slice(0, -1);
    if (base.length >= 2 && base.length <= 4 && /^[A-Z]+$/.test(base)) {
      u = base;
    }
  }
  return u;
}

const INDEX_ROOTS = new Set([
  "SPX", "SPXW", "RUT", "RUTW", "NDX", "NDXP", "XSP", "VIX", "DJX", "OEX", "XEO", "CBTX", "MBTX",
]);

export function isIndexUnderlying(underlying: string): boolean {
  const u = underlying.trim();
  if (u.startsWith("$")) return true;
  return INDEX_ROOTS.has(u.replace(/^\$/, "").toUpperCase());
}

function indexFee(schedule: FeeSchedule, underlying: string): number {
  const root = feeRoot(underlying);
  return schedule.indexFees[root] ?? schedule.indexFees[`$${root}`] ?? 0;
}

/** Commission + reg + (index fee if any) per option contract, one **open** side (uncapped). */
export function feePerContract(schedule: FeeSchedule, underlying: string): number {
  return schedule.optionPerContract + schedule.optionRegPerContract + indexFee(schedule, underlying);
}

function commissionForSide(
  schedule: FeeSchedule,
  underlying: string,
  contracts: number,
  legs: number,
  close: boolean,
): number {
  if (contracts <= 0 || legs <= 0) return 0;
  const rate = close ? schedule.optionClosePerContract : schedule.optionPerContract;
  let perLeg = rate * contracts;
  if (!close) {
    const cap = schedule.optionPerLegCap;
    if (cap != null && cap > 0 && !isIndexUnderlying(underlying)) {
      perLeg = Math.min(perLeg, cap);
    }
  }
  return perLeg * legs;
}

/**
 * Dollar fees for a multi-leg option trade.
 * @param legs number of option legs (vertical = 2)
 * @param sides 1 = open only, 2 = round-trip open+close
 */
export function optionTradeFees(
  schedule: FeeSchedule,
  underlying: string,
  contracts: number,
  legs: number,
  sides: 1 | 2 = 2,
): number {
  if (contracts <= 0 || legs <= 0) return 0;
  const openC = commissionForSide(schedule, underlying, contracts, legs, false);
  const closeC = sides === 2 ? commissionForSide(schedule, underlying, contracts, legs, true) : 0;
  const pass = (schedule.optionRegPerContract + indexFee(schedule, underlying)) * legs * contracts * sides;
  return openC + closeC + pass;
}

/** Parse API schedule (index_fees may arrive as object). */
export function parseFeeSchedule(raw: unknown): FeeSchedule {
  if (!raw || typeof raw !== "object") return { ...DEFAULT_FEE_SCHEDULE };
  const r = raw as Record<string, unknown>;
  const scope = String(r.scope ?? "default");
  const fallback = defaultScheduleForScope(scope);
  const idx = (r.indexFees ?? r.index_fees ?? {}) as Record<string, unknown>;
  const indexFees: Record<string, number> = {};
  for (const [k, v] of Object.entries(idx)) {
    const n = typeof v === "number" ? v : parseFloat(String(v));
    if (!isNaN(n)) indexFees[k.replace(/^\$/, "").toUpperCase()] = n;
  }
  const num = (a: unknown, b: unknown, d: number) => {
    const v = a ?? b;
    const n = typeof v === "number" ? v : parseFloat(String(v ?? ""));
    return isNaN(n) ? d : n;
  };
  const capRaw = r.optionPerLegCap ?? r.option_per_leg_cap;
  let cap: number | null = null;
  if (typeof capRaw === "number" && !isNaN(capRaw)) cap = capRaw;
  else if (capRaw != null) {
    const n = parseFloat(String(capRaw));
    if (!isNaN(n)) cap = n;
  }
  return applyBrokerRules({
    scope,
    equityOnline: num(r.equityOnline, r.equity_online, fallback.equityOnline),
    optionPerContract: num(r.optionPerContract, r.option_per_contract, fallback.optionPerContract),
    optionRegPerContract: num(r.optionRegPerContract, r.option_reg_per_contract, fallback.optionRegPerContract),
    indexFees: Object.keys(indexFees).length ? indexFees : { ...fallback.indexFees },
    optionClosePerContract: num(
      r.optionClosePerContract,
      r.option_close_per_contract,
      fallback.optionClosePerContract,
    ),
    optionPerLegCap: cap,
  });
}

/**
 * Schedule for an account hash. If the API still returns a Schwab-scoped
 * row for a tastytrade hash (old backend), use tastytrade published defaults.
 */
export function scheduleForAccount(accountHash: string, raw: unknown): FeeSchedule {
  const parsed = parseFeeSchedule(raw);
  if (accountHash.startsWith("tastytrade:") && !isTastytradeScope(parsed.scope)) {
    return { ...DEFAULT_TASTYTRADE_FEE_SCHEDULE, scope: "tastytrade:pending" };
  }
  return parsed;
}
